import type { AdminInvoiceAutopayPlan, AdminInvoiceAutopayPreview, AutopayOutcome } from '../../../types/models/Wallet';

// Display copy for the staff "charge now" on an organization's invoice autopay consent
// (docs/api-types/admin-invoice-autopay.md), shared by the Pay-invoice modal's staff block and
// Admin › Wallets › {organization} › Unpaid invoices. Every map is exhaustive over its union, so a new
// backend value fails to compile until it has its copy.

type NoticeVariant = 'success' | 'info' | 'warning' | 'danger';
type LabelColor = 'blue' | 'purple' | 'red' | 'orange' | 'grey' | 'green';

/** Short name of a charge outcome — the notification title's suffix and the run's result label. */
export const AUTOPAY_OUTCOME_LABEL: Record<AutopayOutcome, string> = {
  paid: 'Paid',
  in_flight: 'Card payment in progress',
  declined: 'Card declined',
  no_funds: 'No funds',
  busy: 'Already in progress',
  refused: 'Not collectable',
  not_consented: 'No consent',
  failed: 'Failed',
};

/** Notification variant per outcome: paid → success, in flight → info, not charged → warning, failed → danger. */
export const AUTOPAY_OUTCOME_VARIANT: Record<AutopayOutcome, NoticeVariant> = {
  paid: 'success',
  in_flight: 'info',
  declined: 'warning',
  no_funds: 'warning',
  busy: 'warning',
  refused: 'warning',
  not_consented: 'warning',
  failed: 'danger',
};

export const NOTICE_SR_TITLE: Record<NoticeVariant, string> = {
  success: 'Success alert',
  info: 'Info alert',
  warning: 'Warning alert',
  danger: 'Error alert',
};

export const NOTICE_LABEL_COLOR: Record<NoticeVariant, LabelColor> = {
  success: 'green',
  info: 'blue',
  warning: 'orange',
  danger: 'red',
};

/** What a charge would do today, as a table label. */
export const AUTOPAY_PLAN_LABEL: Record<AdminInvoiceAutopayPlan, { text: string; color: LabelColor }> = {
  wallet: { text: 'Prepaid balance', color: 'blue' },
  card: { text: 'Saved card', color: 'purple' },
  declined: { text: 'Declined before', color: 'red' },
  no_funds: { text: 'No funds', color: 'orange' },
  busy: { text: 'In progress', color: 'grey' },
  refused: { text: 'Not collectable', color: 'grey' },
};

/** Only these plans can be charged; "Charge all" never sends the others. */
export const isPayablePlan = (plan: AdminInvoiceAutopayPlan): plan is 'wallet' | 'card' =>
  plan === 'wallet' || plan === 'card';

/** "Visa •••• 4242" — the card a charge would use (its fields are nullable display data). */
export const cardText = (card: NonNullable<AdminInvoiceAutopayPreview['card']>): string =>
  `${card.card_label ?? 'Saved card'} •••• ${card.card_last4 ?? '????'}`;
